/**
 * Fuzzy matching utilities for preventing duplicate brands/families/models
 * Uses Levenshtein distance algorithm for string similarity
 */

import { Brand } from './brandModelStorage';

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of single-character edits needed to change one string into another
 * 
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Distance (0 = identical, higher = more different)
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Create a 2D array for dynamic programming
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));
  
  // Initialize first column and row
  for (let i = 0; i <= len1; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  // Fill in the rest of the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Calculate similarity score between two strings (0-1, where 1 is identical)
 * 
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Similarity score (0 = completely different, 1 = identical)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  const maxLength = Math.max(str1.length, str2.length);
  
  if (maxLength === 0) return 1;
  
  return 1 - distance / maxLength;
}

/**
 * Normalize a string for comparison
 * - Converts to lowercase
 * - Trims whitespace
 * - Removes special characters
 * - Collapses multiple spaces
 * 
 * @param str - String to normalize
 * @returns Normalized string
 */
export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ');        // Collapse multiple spaces
}

/**
 * Check if a string is an exact match (case-insensitive, normalized)
 * 
 * @param str1 - First string
 * @param str2 - Second string
 * @returns True if exact match
 */
export function isExactMatch(str1: string, str2: string): boolean {
  return normalizeString(str1) === normalizeString(str2);
}

/**
 * Find similar brands based on name and aliases
 * 
 * @param input - User input string
 * @param existingBrands - Array of existing brands
 * @param threshold - Similarity threshold (0-1, default 0.75)
 * @returns Array of similar brands with similarity scores
 */
export function findSimilarBrands(
  input: string,
  existingBrands: Brand[],
  threshold: number = 0.75
): Array<Brand & { similarity: number; matchType: 'exact' | 'alias' | 'fuzzy' }> {
  const normalizedInput = normalizeString(input);
  const results: Array<Brand & { similarity: number; matchType: 'exact' | 'alias' | 'fuzzy' }> = [];
  
  for (const brand of existingBrands) {
    // Check for exact name match
    if (isExactMatch(brand.name, input)) {
      results.push({ ...brand, similarity: 1.0, matchType: 'exact' });
      continue;
    }
    
    // Check aliases for exact match
    const aliases = (brand.aliases as string[]) || [];
    const aliasMatch = aliases.some(alias => isExactMatch(alias, input));
    if (aliasMatch) {
      results.push({ ...brand, similarity: 1.0, matchType: 'alias' });
      continue;
    }
    
    // Check fuzzy match on name
    const nameSimilarity = calculateSimilarity(normalizedInput, normalizeString(brand.name));
    if (nameSimilarity >= threshold) {
      results.push({ ...brand, similarity: nameSimilarity, matchType: 'fuzzy' });
      continue;
    }
    
    // Check fuzzy match on aliases
    for (const alias of aliases) {
      const aliasSimilarity = calculateSimilarity(normalizedInput, normalizeString(alias));
      if (aliasSimilarity >= threshold) {
        results.push({ ...brand, similarity: aliasSimilarity, matchType: 'fuzzy' });
        break; // Only add once per brand
      }
    }
  }
  
  // Sort by similarity (highest first)
  return results.sort((a, b) => b.similarity - a.similarity);
}

/**
 * Check if input is too similar to existing brands
 * Returns the most similar brand if found, null otherwise
 * 
 * @param input - User input string
 * @param existingBrands - Array of existing brands
 * @returns Most similar brand or null
 */
export function checkForDuplicate(
  input: string,
  existingBrands: Brand[]
): (Brand & { similarity: number; matchType: 'exact' | 'alias' | 'fuzzy' }) | null {
  const similar = findSimilarBrands(input, existingBrands, 0.75); // Lowered from 0.85 to 0.75 (75%)
  return similar.length > 0 ? similar[0] : null;
}

/**
 * Format similarity score as percentage
 * 
 * @param similarity - Similarity score (0-1)
 * @returns Formatted percentage string
 */
export function formatSimilarity(similarity: number): string {
  return `${Math.round(similarity * 100)}%`;
}

/**
 * Get suggestion message based on match type
 * 
 * @param matchType - Type of match found
 * @returns User-friendly message
 */
export function getMatchMessage(matchType: 'exact' | 'alias' | 'fuzzy'): string {
  switch (matchType) {
    case 'exact':
      return 'This brand already exists';
    case 'alias':
      return 'This is a known alternative name for an existing brand';
    case 'fuzzy':
      return 'This looks very similar to an existing brand';
    default:
      return 'Similar brand found';
  }
}
